import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

interface RealtimeEnvelope {
  content?: {
    type?: string;
    conversation_id?: string;
    customer_id?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Bus de eventos en tiempo real para toda la app.
 *
 * Mantiene una sola conexión WebSocket por sesión y expone Subjects
 * tipados por feature. Cada componente se suscribe sólo a lo suyo.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeBusService implements OnDestroy {
  private socket: WebSocket | null = null;
  private currentUserId: number | null = null;
  private currentHotelId: number | null = null;
  private manualDisconnect = false;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingPayloads: unknown[] = [];
  private readonly baseUrl = environment.realtimeWebsocketUrl;

  private connectionStatus = new BehaviorSubject<boolean>(false);
  private errorStatus = new BehaviorSubject<string | null>(null);

  // Notifications / alerts (header)
  private refreshNotificationsAlertsSubject = new Subject<boolean>();
  private syncAlertsSubject = new Subject<void>();

  // Concierge
  private newMessageSubject = new Subject<string>();
  private newConversationSubject = new Subject<string>();

  // Service requests (dashboard)
  private serviceRequestSubject = new Subject<boolean>();

  isConnected$ = this.connectionStatus.asObservable();
  error$ = this.errorStatus.asObservable();

  /** Emite `true` para alerts y `false` para news */
  refreshNotificationsAlerts$ = this.refreshNotificationsAlertsSubject.asObservable();
  syncAlerts$ = this.syncAlertsSubject.asObservable();
  newMessage$ = this.newMessageSubject.asObservable();
  newConversation$ = this.newConversationSubject.asObservable();
  serviceRequest$ = this.serviceRequestSubject.asObservable();

  connect(userId: number, hotelId: number): void {
    if (!userId || !hotelId) {
      console.warn('RealtimeBusService.connect called without userId/hotelId');
      return;
    }

    if (
      this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.currentUserId === userId &&
      this.currentHotelId === hotelId
    ) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    this.currentUserId = userId;
    this.currentHotelId = hotelId;
    this.manualDisconnect = false;

    const url = `${this.baseUrl}?userId=${userId}&hotelId=${hotelId}`;

    try {
      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        this.connectionStatus.next(true);
        this.errorStatus.next(null);
        this.flushPendingPayloads();
      };

      this.socket.onclose = (event) => {
        this.connectionStatus.next(false);

        if (!this.manualDisconnect && event.code !== 1000) {
          this.reconnectTimeout = setTimeout(() => {
            if (this.currentUserId && this.currentHotelId) {
              this.connect(this.currentUserId, this.currentHotelId);
            }
          }, 5000);
        }
      };

      this.socket.onerror = (error) => {
        console.error('Realtime WebSocket error:', error);
        this.errorStatus.next('Error de conexión WebSocket');
        this.connectionStatus.next(false);
      };

      this.socket.onmessage = (event) => {
        try {
          const data: RealtimeEnvelope = JSON.parse(event.data);
          this.dispatch(data);
        } catch (e) {
          console.error('Error parsing realtime WS message:', e);
        }
      };
    } catch (error) {
      console.error('Error creating realtime WebSocket connection:', error);
      this.errorStatus.next('Error al crear conexión WebSocket');
    }
  }

  /**
   * Suscribe el socket a una conversación específica de Concierge.
   */
  subscribeToConversation(conversationId: string): void {
    this.send({
      action: 'message',
      type: 'conversation_subscribe',
      conversation_id: conversationId,
    });
  }

  disconnect(): void {
    this.manualDisconnect = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close(1000, 'Disconnected by client');
      this.socket = null;
    }

    this.pendingPayloads = [];
    this.currentUserId = null;
    this.currentHotelId = null;
    this.connectionStatus.next(false);
    this.errorStatus.next(null);
  }

  isConnected(): boolean {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  getConnectionStatus(): boolean {
    return this.connectionStatus.value;
  }

  getCurrentError(): string | null {
    return this.errorStatus.value;
  }

  private send(payload: unknown): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
      return;
    }
    // El socket todavía no está abierto (o se está reconectando): encolamos
    // y mandamos en `onopen`. Si nunca abre, se descarta al desconectar.
    this.pendingPayloads.push(payload);
  }

  private flushPendingPayloads(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const queue = this.pendingPayloads.splice(0);
    queue.forEach((payload) => this.socket!.send(JSON.stringify(payload)));
  }

  private dispatch(data: RealtimeEnvelope): void {
    const type = data?.content?.type;
    if (!type) {
      console.warn('Realtime WS message without content.type:', data);
      return;
    }

    switch (type) {
      case 'refresh_notifications_alerts':
        this.refreshNotificationsAlertsSubject.next(true);
        break;
      case 'refresh_notifications_news':
        this.refreshNotificationsAlertsSubject.next(false);
        break;
      case 'sync_alerts':
        this.syncAlertsSubject.next();
        break;
      case 'message':
        if (data.content?.conversation_id) {
          this.newMessageSubject.next(data.content.conversation_id);
        }
        break;
      case 'conversation_list':
        if (data.content?.customer_id) {
          this.newConversationSubject.next(data.content.customer_id);
        }
        break;
      case 'sync_service_requests':
        this.serviceRequestSubject.next(true);
        break;
      default:
        console.warn('Unknown realtime WS message type:', type, data);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
